import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import PageLayout from '../../layouts/PageLayout';
import { useBranch } from '../../context/BranchContext';
import { useFilterParams } from '../../hooks';
import InvalidParamsNotice from '../../components/InvalidParamsNotice';
import { reservationAPI } from '../../services';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import DateRangePicker, { calcDatePreset } from '../../components/fields/DateRangePicker';

/**
 * #181944 — "Danh sách yêu cầu xác nhận" (lệnh sếp `update_id=731599576`).
 *
 * Sếp cần thấy đúng 3 thứ: AI chịu trách nhiệm xác nhận bàn nào · ĐÃ XÁC NHẬN là gì · hoặc
 * BỎ QUA không xác nhận. Nhóm 🔴 "đã bỏ qua" là nhóm cần nhìn nhất — trước bản vá này chúng bị
 * ghi thành "khách không đến" nên biến mất khỏi tầm nhìn (141/151 ca NO_SHOW 30 ngày).
 *
 * Kế thừa nguyên khuôn `ReservationManagementPage.js`: PageLayout + thẻ tổng hợp + Card lọc +
 * bảng react-bootstrap, chi nhánh đọc từ thanh chọn chi nhánh trên đầu (nguồn duy nhất).
 */

const OUTCOMES = ['ARRIVED', 'NO_SHOW', 'AWAITING', 'SKIPPED'];

const OUTCOME_LABELS = {
  ARRIVED: '✅ Đã xác nhận ĐẾN',
  NO_SHOW: '❌ Đã xác nhận KHÔNG ĐẾN',
  AWAITING: '⏳ Đang chờ xác nhận',
  SKIPPED: '🔴 ĐÃ BỎ QUA, quá hạn không ai xác nhận',
};
const OUTCOME_SHORT = {
  ARRIVED: '✅ Đã đến',
  NO_SHOW: '❌ Không đến',
  AWAITING: '⏳ Đang chờ',
  SKIPPED: '🔴 Đã bỏ qua',
};
const OUTCOME_VARIANT = {
  ARRIVED: 'success',
  NO_SHOW: 'secondary',
  AWAITING: 'warning',
  SKIPPED: 'danger',
};

const ROLE_LABELS = { ADMIN: 'Admin', MANAGER: 'Quản lý', STAFF: 'Nhân viên' };

const fmtDateTime = (iso) => {
  if (!iso) return '-';
  try {
    return format(new Date(iso), 'dd/MM/yyyy HH:mm');
  } catch {
    return '-';
  }
};

/** 95 phút → "1h35p" (nhìn nhanh mức trễ, không phải nhẩm). */
const fmtOverdue = (minutes) => {
  if (minutes == null) return '-';
  if (minutes < 60) return `${minutes} phút`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h${String(m).padStart(2, '0')}p` : `${h} giờ`;
};

const staffLabel = (e) => {
  if (!e) return null;
  const role = ROLE_LABELS[e.role] || e.role || '';
  return role ? `${e.full_name} (${role})` : e.full_name;
};

const isIsoDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v));

const FILTER_SPEC = {
  from: { default: () => calcDatePreset('last7days').from, valid: isIsoDate },
  to: { default: () => calcDatePreset('last7days').to, valid: isIsoDate },
  outcome: {
    default: '',
    parse: (v) => v.toUpperCase(),
    valid: (v) => OUTCOMES.includes(v.toUpperCase()),
  },
};

const ArrivalCheckRequestPage = () => {
  const { selectedBranch } = useBranch();
  const [filters, setFilters, , { ignored, dismissIgnored }] = useFilterParams(FILTER_SPEC);
  const [search, setSearch] = useState('');

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  // Modal sửa lại phiếu bị đánh nhầm "không đến"
  const [fixing, setFixing] = useState(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchRows = useCallback(async () => {
    if (!selectedBranch?.id) {
      setRows([]);
      setSummary(null);
      return;
    }
    if (!filters.from || !filters.to) return; // tránh Invalid Date khi xoá ô ngày
    try {
      setLoading(true);
      const response = await reservationAPI.getArrivalChecks({
        branchId: selectedBranch.id,
        from: new Date(`${filters.from}T00:00:00`).toISOString(),
        to: new Date(`${filters.to}T23:59:59`).toISOString(),
      });
      // apiClient có thể trả nguyên body `{data, summary}` hoặc đã bóc sẵn `data` —
      // nhận cả hai, giống cách ReservationManagementPage đang làm.
      const body = response?.data && !Array.isArray(response.data) ? response.data : response;
      const list = Array.isArray(body?.data) ? body.data : Array.isArray(response?.data) ? response.data : [];
      setRows(list);
      setSummary(body?.summary ?? response?.summary ?? null);
    } catch (error) {
      toast.error(error?.message || 'Không tải được danh sách yêu cầu xác nhận');
    } finally {
      setLoading(false);
    }
  }, [selectedBranch, filters.from, filters.to]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Lọc trạng thái + tìm kiếm ở client, giống trang Quản lý đặt bàn (dữ liệu 1 trang, 1 lần gọi).
  const filtered = useMemo(() => {
    const kw = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filters.outcome && r.outcome !== filters.outcome) return false;
      if (kw) {
        const hay = `${r.customer_name || ''} ${r.customer_phone || ''}`.toLowerCase();
        if (!hay.includes(kw)) return false;
      }
      return true;
    });
  }, [rows, filters.outcome, search]);

  const counts = useMemo(() => {
    const c = { ARRIVED: 0, NO_SHOW: 0, AWAITING: 0, SKIPPED: 0 };
    rows.forEach((r) => { c[r.outcome] = (c[r.outcome] || 0) + 1; });
    return c;
  }, [rows]);

  const handleReset = () => {
    const r = calcDatePreset('last7days');
    setFilters({ from: r.from, to: r.to, outcome: '' });
    setSearch('');
  };

  const submitFix = async () => {
    if (!fixing) return;
    if (!reason.trim()) {
      toast.warn('Phải ghi lý do sửa lại');
      return;
    }
    try {
      setSaving(true);
      const res = await reservationAPI.resetArrivalCheck(fixing.id, {
        reason: reason.trim(),
        mark_as: 'ARRIVED',
      });
      toast.success(res?.data?.message || res?.message || 'Đã sửa lại');
      setFixing(null);
      setReason('');
      fetchRows();
    } catch (err) {
      toast.error(err?.message || 'Không sửa được');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout>
      <div className="mc-page">
        <div className="mc-page-header my-2">
          <div className="mc-page-header-title">
            <h1>Yêu cầu xác nhận đặt bàn</h1>
            <small className="text-muted">
              {selectedBranch?.name ? `Chi nhánh: ${selectedBranch.name}` : 'Vui lòng chọn chi nhánh'}
            </small>
          </div>
          <div className="mc-page-header-actions d-flex gap-2">
            <Button variant="outline-secondary" onClick={handleReset} disabled={loading}>
              Đặt lại bộ lọc
            </Button>
            <Button variant="primary" onClick={fetchRows} disabled={loading}>
              {loading ? 'Đang tải...' : 'Làm mới'}
            </Button>
          </div>
        </div>

        <InvalidParamsNotice ignored={ignored} onDismiss={dismissIgnored} />

        <Row className="mb-3">
          {OUTCOMES.map((o) => (
            <Col md key={o} className="mb-3">
              <Card
                className={`h-100 shadow-sm border-${OUTCOME_VARIANT[o]}`}
                role="button"
                onClick={() => setFilters({ outcome: filters.outcome === o ? '' : o })}
              >
                <Card.Body>
                  <small className="text-muted text-uppercase">{OUTCOME_SHORT[o]}</small>
                  <h3 className={`text-${OUTCOME_VARIANT[o]} mb-0`}>{counts[o] || 0}</h3>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Card className="mb-3 shadow-sm">
          <Card.Body>
            <Row className="g-3 align-items-end">
              <Col md={5}>
                <DateRangePicker
                  fromDate={filters.from}
                  toDate={filters.to}
                  onChange={(from, to) => setFilters({ from, to })}
                  defaultPreset="last7days"
                />
              </Col>
              <Col md={4}>
                <Form.Label>Trạng thái xác nhận</Form.Label>
                <Form.Select
                  value={filters.outcome}
                  onChange={(e) => setFilters({ outcome: e.target.value })}
                >
                  <option value="">Tất cả</option>
                  {OUTCOMES.map((o) => (
                    <option key={o} value={o}>{OUTCOME_LABELS[o]}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label>Tìm khách / SĐT</Form.Label>
                <Form.Control
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Tên hoặc số điện thoại"
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="shadow-sm">
          <Card.Body className="p-0">
            <div className="table-responsive">
              {/* Khổ điện thoại 390: để bảng giữ bề rộng thật và CUỘN NGANG trong
                  `.table-responsive`, thay vì bị bóp còn 1-2 chữ mỗi dòng. Cùng cách hiển thị
                  với bảng của "Quản lý đặt bàn" (đo thật: bảng 849px trong khung 356px). */}
              <Table hover className="mb-0 align-middle" style={{ minWidth: 900 }}>
                <thead>
                  <tr>
                    <th>Đặt bàn</th>
                    <th>Chỗ</th>
                    <th>Ai chịu trách nhiệm xác nhận</th>
                    <th>Kết quả</th>
                    <th>Ai bấm · lúc nào</th>
                    <th>Quá hạn</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={7} className="text-center py-4">
                        <Spinner animation="border" size="sm" /> Đang tải...
                      </td>
                    </tr>
                  )}
                  {!loading && filtered.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        Không có yêu cầu xác nhận nào trong khoảng đã chọn
                      </td>
                    </tr>
                  )}
                  {!loading && filtered.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <div className="fw-semibold">{r.customer_name}</div>
                        <small className="text-muted d-block">{r.customer_phone || '-'}</small>
                        <small className="text-muted">
                          {fmtDateTime(r.reservation_time)} · {r.guest_count} khách
                        </small>
                      </td>
                      <td>
                        <div>{r.space_type === 'PRIVATE' ? (r.room_name || 'Phòng riêng') : 'Không gian chung'}</div>
                        <small className="text-muted">
                          {r.table_number ? `Bàn ${r.table_number}` : ''} {r.branch_name}
                        </small>
                      </td>
                      <td>
                        {r.responsible?.length
                          ? r.responsible.map((e) => (
                              <div key={e.id}><small>{staffLabel(e)}</small></div>
                            ))
                          : <small className="text-danger">Không có ai trong ca</small>}
                      </td>
                      <td>
                        <Badge bg={OUTCOME_VARIANT[r.outcome]}>{OUTCOME_SHORT[r.outcome]}</Badge>
                        {r.escalated_at && (
                          <div><small className="text-muted">Đã báo quản lý {fmtDateTime(r.escalated_at)}</small></div>
                        )}
                      </td>
                      <td>
                        {r.checked_by ? (
                          <>
                            <div><small>{staffLabel(r.checked_by)}</small></div>
                            <small className="text-muted">{fmtDateTime(r.checked_at)}</small>
                          </>
                        ) : (
                          <small className="text-muted">
                            chưa ai bấm
                            {r.reminded_at && <div>đã giục {fmtDateTime(r.reminded_at)}</div>}
                          </small>
                        )}
                      </td>
                      <td>
                        {r.overdue_minutes != null ? (
                          <span className={r.outcome === 'SKIPPED' ? 'text-danger fw-semibold' : ''}>
                            {fmtOverdue(r.overdue_minutes)}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="text-end">
                        {(r.outcome === 'SKIPPED' || r.outcome === 'NO_SHOW') && (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => { setFixing(r); setReason(''); }}
                          >
                            Khách CÓ đến
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        {summary && (
          <small className="text-muted d-block mt-2">
            Đang hiện {filtered.length} / {rows.length} phiếu cần xác nhận.
          </small>
        )}

        <Modal show={!!fixing} onHide={() => setFixing(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Sửa lại: khách CÓ đến</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-2">
              <strong>{fixing?.customer_name}</strong> — {fmtDateTime(fixing?.reservation_time)}
            </p>
            <p className="text-muted small">
              Phiếu này đang bị ghi là khách không đến. Sửa lại sẽ ghi nhật ký đích danh người sửa,
              thời điểm và lý do.
            </p>
            <Form.Label>Lý do (bắt buộc)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="VD: khách có đến, nhân viên quên bấm xác nhận"
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setFixing(null)} disabled={saving}>
              Đóng
            </Button>
            <Button variant="primary" onClick={submitFix} disabled={saving}>
              {saving ? 'Đang lưu...' : 'Xác nhận sửa'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </PageLayout>
  );
};

export default ArrivalCheckRequestPage;
